import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Stack,
  Alert,
  MenuItem,
} from "@mui/material";

export default function EvLeasingSection() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    interestType: "Franchise Partner",
    investmentTier: "1 - 5 EVs",
    city: "",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.fullName && formData.phone) {
      setSubmitted(true);
    }
  };

  return (
    <Box
      id="franchise-invest"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: "background.paper",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            borderRadius: { xs: "16px", md: "24px" },
            overflow: "hidden",
            boxShadow: "0 20px 45px -15px rgba(27, 78, 216, 0.15)",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Grid container alignItems="stretch">
            {/* Left Column - Deep Navy Blue (#1B4ED8) */}
            <Grid
              item
              xs={12}
              md={6.5}
              sx={{
                bgcolor: "primary.main", // Deep Navy Blue (#1B4ED8) matching new RideX City Logo
                p: { xs: 3.5, sm: 5, md: 6 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                color: "#FFFFFF",
              }}
            >
              <Box>
                {/* Banner Photo */}
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: 200, sm: 230 },
                    borderRadius: "14px",
                    overflow: "hidden",
                    mb: 3.5,
                    border: "2px solid rgba(255, 255, 255, 0.25)",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/images/ev-leasing-banner.jpg"
                    alt="Franchise & Investor EV fleet"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center 30%",
                      display: "block",
                    }}
                  />
                </Box>

                {/* Accent Tag - Electric Lime Green */}
                <Box
                  sx={{
                    display: "inline-block",
                    px: 1.75,
                    py: 0.6,
                    borderRadius: "6px",
                    bgcolor: "secondary.main", // #72BF20 Lime Green
                    color: "#FFFFFF",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    mb: 2,
                    boxShadow: "0 4px 12px rgba(114, 191, 32, 0.3)",
                  }}
                >
                  FRANCHISE & INVEST OPPORTUNITY
                </Box>

                {/* Main Heading */}
                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: { xs: 28, sm: 34, md: 38 },
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                    color: "#FFFFFF",
                    mb: 1.5,
                  }}
                >
                  Franchise & Invest with RideX
                </Typography>

                {/* Subtitle */}
                <Typography
                  sx={{
                    fontSize: { xs: 14.5, sm: 16 },
                    color: "rgba(255, 255, 255, 0.9)",
                    fontWeight: 400,
                    lineHeight: 1.5,
                    mb: 3,
                  }}
                >
                  Own an EV fleet franchise or invest in high-yield green mobility backed by long-term contracts with India's top delivery companies.
                </Typography>
              </Box>

              {/* Partner Brand Badges */}
              <Box
                sx={{
                  pt: 2.5,
                  borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  {["Zomato", "Swiggy", "Blinkit", "Zepto", "Amazon"].map((brand) => (
                    <Box
                      key={brand}
                      sx={{
                        px: 2,
                        py: 0.6,
                        borderRadius: "6px",
                        bgcolor: "rgba(255, 255, 255, 0.15)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#FFFFFF",
                          letterSpacing: "0.03em",
                        }}
                      >
                        {brand}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            {/* Right Column - Clean White Form */}
            <Grid
              item
              xs={12}
              md={5.5}
              sx={{
                p: { xs: 4, sm: 5, md: 6 },
                bgcolor: "background.paper",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box sx={{ textAlign: "left", mb: 3.5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: { xs: 26, md: 30 },
                    fontWeight: 700,
                    color: "primary.main",
                    mb: 0.75,
                  }}
                >
                  Franchise & Investor Enquiry
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                  Fill in your details below to receive our official Franchise Deck and Investment ROI proposal.
                </Typography>
              </Box>

              {submitted ? (
                <Alert severity="success" sx={{ borderRadius: "12px", py: 2 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                    Thank you, {formData.fullName}!
                  </Typography>
                  <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                    Our Franchise & Investment team will contact you within 24 hours with full details.
                  </Typography>
                </Alert>
              ) : (
                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", mb: 0.75 }}>
                        Full Name *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. Rahul Sharma"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            bgcolor: "#F8F9FA",
                            height: 46,
                            "& fieldset": { borderColor: "#E2E8F0" },
                            "&:hover fieldset": { borderColor: "primary.main" },
                            "&.Mui-focused fieldset": { borderColor: "primary.main" },
                          },
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", mb: 0.75 }}>
                        Email Address *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="rahul@example.com"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            bgcolor: "#F8F9FA",
                            height: 46,
                            "& fieldset": { borderColor: "#E2E8F0" },
                            "&:hover fieldset": { borderColor: "primary.main" },
                            "&.Mui-focused fieldset": { borderColor: "primary.main" },
                          },
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", mb: 0.75 }}>
                        Phone Number *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="+91 98765 43210"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            bgcolor: "#F8F9FA",
                            height: 46,
                            "& fieldset": { borderColor: "#E2E8F0" },
                            "&:hover fieldset": { borderColor: "primary.main" },
                            "&.Mui-focused fieldset": { borderColor: "primary.main" },
                          },
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", mb: 0.75 }}>
                        Interest Type *
                      </Typography>
                      <TextField
                        fullWidth
                        select
                        name="interestType"
                        value={formData.interestType}
                        onChange={handleChange}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            bgcolor: "#F8F9FA",
                            height: 46,
                            "& fieldset": { borderColor: "#E2E8F0" },
                            "&:hover fieldset": { borderColor: "primary.main" },
                            "&.Mui-focused fieldset": { borderColor: "primary.main" },
                          },
                        }}
                      >
                        {[
                          "Franchise Partner",
                          "EV Investor",
                          "Fleet Operator / Leasing",
                          "Both Franchise & Investment",
                        ].map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>

                    {/* Cleanly Labeled Side-by-Side Row */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", mb: 0.75 }}>
                          Investment / Fleet Size
                        </Typography>
                        <TextField
                          fullWidth
                          select
                          name="investmentTier"
                          value={formData.investmentTier}
                          onChange={handleChange}
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              bgcolor: "#F8F9FA",
                              height: 46,
                              "& fieldset": { borderColor: "#E2E8F0" },
                              "&:hover fieldset": { borderColor: "primary.main" },
                              "&.Mui-focused fieldset": { borderColor: "primary.main" },
                            },
                          }}
                        >
                          {[
                            "1 - 5 EVs",
                            "5 - 15 EVs",
                            "15 - 50 EVs",
                            "50+ EVs (Master Franchise)",
                          ].map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", mb: 0.75 }}>
                          City *
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="e.g. Bengaluru"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              bgcolor: "#F8F9FA",
                              height: 46,
                              "& fieldset": { borderColor: "#E2E8F0" },
                              "&:hover fieldset": { borderColor: "primary.main" },
                              "&.Mui-focused fieldset": { borderColor: "primary.main" },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        height: 50,
                        borderRadius: "10px",
                        fontSize: 16,
                        fontWeight: 700,
                        fontFamily: "'Space Grotesk', sans-serif",
                        boxShadow: "0 6px 20px rgba(27, 78, 216, 0.25)",
                        mt: 1,
                        "&:hover": {
                          bgcolor: "primary.dark",
                          boxShadow: "0 10px 25px rgba(27, 78, 216, 0.35)",
                        },
                      }}
                    >
                      Request Proposal & Call Back
                    </Button>
                  </Stack>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
